/*
 * CORPORATE GIFT PRICES, in the browser: window.CFKitPrice.
 *
 * One copy for the gift page (assets/corporate-kit.js) and the corporate collection
 * (sections/main-collection-kits.liquid), so the two can never quote a gift differently. The
 * numbers are the shop's one price list (shop metafield cityframes.kit_prices, every
 * corporate_gift_option once, written by tools/kits and proven against the corporate
 * configurator), and a gift is added up from its own entries (the variant's custom.kit_contents).
 * The arithmetic is tools/kits/kit_math.mjs, which the cart function carries too
 * (cityframes-cart-transform, bundle-price src/kits.js): keep the three in step, or a page and
 * the cart disagree.
 *
 *   const kp = CFKitPrice.create(list, country, { <variant id>: [<entry id>, ...] }, { ship });
 *   kp.kit(kitId, services)                            ->  the gift's sums, or null
 *   kp.quote(kitId, { qty, model, region, services, express }) -> { k, custom, each, totalNet, totalGross } | null
 *   kp.freight(k, qty, region, express)                ->  the delivery in the price, EUR net
 *   kp.byFreight(k, qty, region, express)              ->  true: the pieces go by freight (a pallet), not parcels
 *   kp.freightFrom(k, region, express)                 ->  the pieces from which they go by freight, or null: never
 *   kp.expressDelivery(region)                         ->  true: the list prices Express delivery there
 *   kp.readyDays(k, qty, custom, stock)                ->  { days, calendar, extended }: days until it ships, calendar
 *                                                          days by the entries' own (2026-11-03; before, business days)
 *   kp.transit(k, qty, region, express)                ->  "1-3": business days on the way, parcel or freight
 *   kp.deliveryDays(kitId, { qty, model, region, stock }) ->  order to door, the longer transit (kp.calendar: in calendar days)
 *   kp.express(k, qty, custom, stock)                  ->  true: nothing about the gift adds to its production time
 *
 * `model` is 'city' or 'custom'; a gift whose entries name a model quotes that one only, as the
 * cart prices it. `services` are the design services the buyer adds, by part ('card', 'plaque',
 * ...), each one the gift's parts offer. `region` is where the gifts go, the country's own: its
 * freight zone since 2026-11-03 (DE, EU, EUROPE, ASIA, NA, GULF, WORLD; the list's
 * freight.countries), DE, EU or NONEU for a list from before the zones. `kp.taxRegion` is the
 * country's DE, EU or NONEU either way, for what depends on VAT (the list's `zones`). VAT is always
 * the country's: it is what the cart charges. `express` is Express delivery (Alex, 2026-10-31):
 * the zone's Express rows (before them the list's freight.express factors), as the cart prices a
 * mark with |express. (kp.express is something else: a gift nothing adds production time to.)
 * `ship` is the country's transit times from its tax entry (business days, [first, last]):
 * standard (DHL in Germany, else UPS Standard or Expedited), express (UPS Express), freight and
 * freightExpress (the freight and freight Express times) - the zones' transit; a list from before
 * them has its regions' own.
 */
(() => {
  if (window.CFKitPrice) return;

  const stepPrice = (steps, qty) => {
    let out = 0;
    for (const [from, value] of steps || []) if (qty >= from) out = value;
    return out;
  };

  const roundTo = (amount, decimals) => Math.round(amount * 10 ** decimals + 1e-9) / 10 ** decimals;

  const round6 = (n) => Math.round(n * 1e6) / 1e6;

  /* A gift reduced to its sums, from its entries and the list: kitFromContents in
     tools/kits/kit_math.mjs, line for line. */
  function kitFromContents(list, ids, services = []) {
    if (!list || !list.options || !Array.isArray(ids) || !ids.length) return null;
    const opts = [];
    for (const id of ids) {
      const o = list.options[String(id)];
      if (!o) return null;
      opts.push(o);
    }
    const size = (opts.find((o) => o.g === 'size') || {}).c;
    if (!size || !list.sizes || !list.sizes[size]) return null;
    const model = opts.find((o) => o.g === 'model');
    const box = opts.find((o) => o.g === 'box');
    const cover = opts.find((o) => o.g === 'cover');
    const magnetic = Boolean(box && (box.c === 'premium_plain' || box.c === 'premium_custom'));
    const twoBoxes = Boolean(cover) && !magnetic;

    const priced = opts.filter((o) => o.g !== 'size' && o.g !== 'model' && o.g !== 'services'
      && !(o.g === 'coverBox' && (!twoBoxes || o.c === 'product_cf')));
    let u = 0;
    let f = 0;
    for (const o of priced) {
      u += (o.u || 0) * (o.g === 'sleeve' && twoBoxes ? 2 : 1);
      f += o.f || 0;
    }

    const offered = {};
    for (const o of priced) {
      const s = o.ds && list.options[String(o.ds)];
      if (s && (!offered[s.c] || (s.f || 0) > (offered[s.c].f || 0))) offered[s.c] = s;
    }
    for (const choice of services) {
      const s = offered[choice];
      if (!s) return null;
      f += s.f || 0;
    }

    const customEntry = Object.values(list.options).find((o) => o.g === 'model' && o.c === 'custom_model'
      && (size === 'cube' ? o.k : o.m));
    const models = model ? [model.c] : ['available', 'custom_model'];
    const k = { size, u: round6(u), f: round6(f), cover: Boolean(cover), coverType: cover ? cover.c : null, magnetic, models,
      services: Object.keys(offered).sort() };
    k.custom = models.includes('custom_model') && customEntry && customEntry.s && customEntry.hr > 0 ? customEntry.f || 0 : null;
    if (k.custom != null) k.data = { id: String(customEntry.s), qty: 1, hours: customEntry.hr };

    const plaque = opts.find((o) => o.g === 'plaque');
    if (plaque) {
      if (!plaque.s) return null;
      k.plaque = { id: String(plaque.s), u: plaque.u || 0, f: plaque.f || 0, restock: plaque.r || 0 };
    }
    if (cover) {
      if (!cover.s) return null;
      k.coverPart = { id: String(cover.s), u: cover.u || 0, f: cover.f || 0, restock: cover.r || 0 };
    }

    const days = Math.max(0, ...priced.map((o) => o.d || 0));
    k.days = { available: days, custom: Math.max(days, (customEntry && customEntry.d) || 0) };
    k.dataDays = (customEntry && customEntry.dd) || 0;
    /* PRODUCTION BY THE ENTRIES (2026-11-03): the size entry's days by quantity - the model's own - and those of every
       option that counts, each with whether it is ordered during the 3D data work and, for a stock part, its stock
       item and restock days. A list from before carries no `p`: the old base tiers and minimum days count then. */
    const sizeEntry = opts.find((o) => o.g === 'size');
    if (Array.isArray(sizeEntry.p)) {
      k.production = [sizeEntry, ...priced].map((o) => ({ p: Array.isArray(o.p) ? o.p : [], w: Boolean(o.w),
        s: o.s ? String(o.s) : '', r: Number(o.r) || 0 }));
    }
    return k;
  }

  /* The covers' order in a zone's rows; a fourth, where a zone has one, is the magnetic box's. */
  const COVERS = ['none', 'acrylic', 'glass'];
  const MAGNETIC_ROW = 3;

  function create(cfg, country, contents = {}, { ship: shipDays = {} } = {}) {
    const fr = cfg.freight || {};
    const zoned = Boolean(fr.zones);
    /* The country's freight zone (the one listing it, WORLD for one in none), or before the zones its region. */
    const zoneOf = (code) => {
      for (const zone of Object.keys(fr.countries || {})) if (String(fr.countries[zone]).split(',').includes(code)) return zone;
      return 'WORLD';
    };
    const taxRegion = (cfg.zones && cfg.zones[country]) || 'NONEU';
    const region = zoned ? zoneOf(String(country || '')) : taxRegion;
    const vat = Number((cfg.vat && cfg.vat[country]) || 0);
    const rate = Number(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    const decimals = Number((cfg.decimals && cfg.decimals[currency]) ?? 2);

    /* A ZONE'S ROW for the speed and the gift's cover: [parcel base, parcel per piece, freight base, freight per
       piece, freight from], or null where the list has none (the cart then sells nothing). A magnetic box has a
       fourth row where the zone has one, by freight from 0 pieces, whatever cover goes into the box. */
    const rowFor = (k, to, express) => {
      const rows = fr.zones && fr.zones[to] && fr.zones[to][express ? 'x' : 's'];
      const row = rows && rows[k.magnetic && Array.isArray(rows[MAGNETIC_ROW]) ? MAGNETIC_ROW
        : Math.max(0, COVERS.indexOf(k.coverType || 'none'))];
      return Array.isArray(row) && row.length >= 5 ? row : null;
    };
    const mini = (k) => k.size === 'minicube';
    /* The pieces from which the gifts go by freight (a miniCUBE's twice the row's), or null: never. */
    const freightFrom = (k, to = region, express = false) => {
      if (!zoned) return fr.palletFrom[k.size];
      const row = rowFor(k, to, express);
      return row && row[4] != null ? Number(row[4]) * (mini(k) ? 2 : 1) : null;
    };
    const byFreight = (k, qty, to = region, express = false) => {
      const from = freightFrom(k, to, express);
      return from != null && qty >= from;
    };

    /* Before the zones: Express delivery's factors on the base and the per-unit freight, or none for Standard. */
    const ONE = { base: 1, unit: 1 };
    const speed = (express) => (express ? fr.express : ONE);
    const expressDelivery = (to = region) => (zoned ? Boolean(fr.zones[to] && fr.zones[to].x)
      : Boolean(fr.express && fr.express.base > 0 && fr.express.unit > 0));

    /* EUR net freight per piece for `qty` pieces to `to`, the cart's own arithmetic (kits.js freightPerPiece): the
       zone's row, by parcel or by freight, a miniCUBE half the per-piece rate; before the zones parcel or pallet by
       quantity, doubled with a display cover, Express's factors on top. NaN where the list has none. */
    const shipEach = (k, qty, to, express) => {
      if (zoned) {
        const row = rowFor(k, to, express);
        if (!row) return NaN;
        const viaFreight = byFreight(k, qty, to, express);
        const base = Number(viaFreight ? row[2] : row[0]);
        const unit = Number(viaFreight ? row[3] : row[1]) * (mini(k) ? 0.5 : 1);
        return (base + unit * qty) / qty;
      }
      const mode = qty >= fr.palletFrom[k.size] ? 'pallet' : 'parcel';
      const r = fr[mode][to];
      if (!r) return NaN;
      const x = speed(express);
      let each = (r.base * x.base + r.unit[k.size] * x.unit * qty) / qty;
      if (k.cover) each *= fr.cover;
      return each;
    };

    /* EUR net freight for `qty` pieces to `to`, Express when `express`. */
    const freight = (k, qty, to = region, express = false) => shipEach(k, qty, to, express) * qty;

    /* EUR net per piece, the configurator's price() reduced: the size's step, the options' prices per piece, freight
       per piece, the one-offs spread - in the function's own order, so a price can never land a rounding away from
       the cart's. */
    const perPiece = (k, custom, qty, to = region, express = false) =>
      stepPrice(cfg.sizes[k.size], qty) + k.u + shipEach(k, qty, to, express) + (k.f + (custom ? k.custom : 0)) / qty;

    /* The gift's sums with the chosen design services, or null for one the cart would not sell:
       an unknown or blocked gift, a service its parts do not offer. */
    const kits = {};
    function kit(kitId, services = []) {
      const key = kitId + '|' + services.join('+');
      if (!(key in kits)) {
        kits[key] = cfg.blocked && cfg.blocked[kitId] ? null : kitFromContents(cfg, contents[kitId], services);
      }
      return kits[key];
    }

    /* A gift for `qty` pieces: net per piece (a custom model spread over them, for reading), the
       net total, and the total with VAT exactly as the cart builds it - per piece with VAT on,
       times the pieces, plus the data work, one Data creation of the model's hours. Null for a
       model the gift is not sold as. */
    function quote(kitId, { qty, model = 'city', region: to = region, services = [], express = false }) {
      if (express && !expressDelivery(to)) return null;
      const k = kit(kitId, services);
      if (!k) return null;
      if (!k.models.includes(model === 'custom' ? 'custom_model' : 'available')) return null;
      const units = k.data ? Number(k.data.qty) || 1 : 0;
      const custom = model === 'custom' && k.custom != null && units > 0;
      if (model === 'custom' && !custom) return null;
      const withVat = 1 + vat / 100;
      if (!Number.isFinite(perPiece(k, false, qty, to, express))) return null;
      const eachNet = roundTo(perPiece(k, false, qty, to, express) * rate, decimals);
      const unitNet = custom ? roundTo((k.custom / units) * rate, decimals) : 0;
      const totalNet = roundTo(eachNet * qty + (custom ? unitNet * units : 0), decimals);
      const eachGross = roundTo(perPiece(k, false, qty, to, express) * rate * withVat, decimals);
      const unitGross = custom ? roundTo((k.custom / units) * rate * withVat, decimals) : 0;
      const totalGross = roundTo(eachGross * qty + (custom ? unitGross * units : 0), decimals);
      // The pieces and the custom model's one-off apart, net, for the PDP's total (2026-10-28).
      return { k, custom, each: totalNet / qty, eachNet, customNet: custom ? roundTo(unitNet * units, decimals) : 0, totalNet, totalGross };
    }

    /* Production as the configurator counts it - the quantity's own days against the options' minimums - with a stock
       item's restock days joining that max once the pieces outrun its stock (they run alongside production, not
       after it), and a custom model's 3D data days on top. `stock` is each stock item's count, by variant id. */
    function readyDays(k, qty, custom, stock = {}) {
      const data = custom ? Number(k.dataDays) || 0 : 0;
      if (k.production) {
        /* As the configurator counts it (production in assets/corporate-gift-configurator.js), in calendar days: each
           entry's days at the quantity - or a stock part's restock days where its stock is short of the pieces - the
           longest counting, less the 3D data work for one ordered during it; the data work on top. `extended`: an
           option or a restock reaches past the model's own days. */
        const own = (o) => {
          const short = o.r > 0 && o.s && qty > Number(stock[o.s] ?? Infinity);
          return Math.max(stepPrice(o.p, qty), short ? o.r : 0) - (o.w ? data : 0);
        };
        const days = Math.max(0, ...k.production.map(own));
        return { days: days + data, calendar: true, extended: days > own(k.production[0]) };
      }
      const base = Math.max(stepPrice(cfg.production && cfg.production.baseTiers, qty), Number(k.days && k.days[custom ? 'custom' : 'available']) || 0);
      const restock = Math.max(0, ...[k.plaque, k.coverPart]
        .filter((part) => part && qty > Number(stock[part.id] ?? Infinity))
        .map((part) => Number(part.restock) || 0));
      return { days: Math.max(base, restock) + data, restocked: restock > base };
    }

    /* EXPRESS (Alex, 2026-10-29: "option-based, but removed when anything adds to the production time, like displays
       not in stock"): no option of the gift has production days of its own (a colour, trees on a Custom Model, a
       sleeve, a printed or magnetic box - the mockup's rule, whatever the quantity), and for the pieces no part has to
       be restocked in more time than production takes (a cover or plaque short of them). A Custom Model's 3D work is
       the model's, not the gift's. */
    function express(k, qty, custom, stock = {}) {
      if (k.production) return !readyDays(k, qty, custom, stock).extended;
      const own = Number(k.days && k.days[custom ? 'custom' : 'available']) || 0;
      return own === 0 && !readyDays(k, qty, custom, stock).restocked;
    }

    /* The transit time for the way the pieces travel, "1-3": by parcel or by freight, Standard or Express - the
       country's own times since the zones (`ship`), before them the region's from the list. */
    const days = (list) => (Array.isArray(list) && list.length ? list.join('-') : '');
    const transit = (k, qty, to = region, express = false) => {
      if (zoned) {
        const viaFreight = byFreight(k, qty, to, express);
        return days(express ? (viaFreight ? shipDays.freightExpress : shipDays.express)
          : (viaFreight ? shipDays.freight : shipDays.standard));
      }
      const mode = qty >= fr.palletFrom[k.size] ? 'pallet' : 'parcel';
      return String((cfg.transit && cfg.transit[to] || {})[mode] || '');
    };

    /* Order to door, as the gift page says it ("Ready to ship in …, then … to you"): ready, plus the longer end of the
       transit - in business days before the entries' own production days, in calendar days since (`calendar`), the
       transit's business days as calendar days then. Null for a gift or model the cart would not sell. */
    const calendar = Object.values(cfg.options || {}).some((o) => o.g === 'size' && Array.isArray(o.p));
    function deliveryDays(kitId, { qty, model = 'city', region: to = region, stock = {} }) {
      const q = quote(kitId, { qty, model, region: to });
      if (!q) return null;
      const way = transit(q.k, qty, to).split('-').map(Number).filter((n) => !Number.isNaN(n));
      const last = way.length ? Math.max(...way) : 0;
      return readyDays(q.k, qty, q.custom, stock).days + (calendar ? Math.ceil((last * 7) / 5) : last);
    }

    return { zoned, calendar, region, taxRegion, vat, rate, currency, decimals, stepPrice, roundTo, perPiece, freight, byFreight, freightFrom, expressDelivery, kit, quote, readyDays, express, transit, deliveryDays };
  }

  window.CFKitPrice = { create, stepPrice, roundTo, kitFromContents };
})();
